import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineFlag,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlinePrint,
    MdSuperscript
} from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useDeviceOS from 'hooks/useDeviceOS';
import { canRequestEdits, buildEditRequestUrl } from 'global/editRequest';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from 'components/Reference';
import Shortcuts from 'components/Shortcuts';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};


function DigitalSATReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [print, setPrint] = useState(false);

    const { user } = useAuthContext();
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate(`/report/${testId}`);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('testReview__passages');
        let question = document.getElementById('testReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const generateAnnotation = () => {
        const newAnnotations = [ ...testModules[moduleIndex].answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            // A choice highlight's element id is PREFIXED (`choice-<id>`),
            // because choice ids and question ids come from different tables
            // and a bare id could collide in getElementById. Unprefixed, the
            // lookup found nothing, hit the `continue` below and left `html`
            // undefined -- so the overlay rendered empty and the highlight was
            // missing from review even though it had saved correctly.
            const passage = newAnnotations[i].isChoice
                ? document.getElementById(`choice-${passageId}`)
                : document.getElementById(passageId);

            // Stem annotations reference a question id with no matching
            // passage element on the review page; skip rather than crash.
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const handleAnnotationMouseOver = (
        // HTMLElement, not HTMLDivElement: passage overlays are <div> but a
        // choice overlay is a <span> (it lives inside a <button>). Only
        // clientX/clientY are read, which any element's event carries.
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.left = event.clientX + 'px';
            bubble.style.top = event.clientY + 'px';
            bubble.style.display = 'block';
        }

        return false;
    }

    const handleAnnotationMouseOut = (
        _event: React.MouseEvent<HTMLElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.display = 'none';
        }

        return false;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            // console.log(wrappingTexts[i]);
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'KeyT':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyV':
                //     markQuestion();
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyR':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit5':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit6':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                // case 'Digit7':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit8':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit9':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit0':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                // case 'KeyG':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyH':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => setPrint(false)
    });

    useEffect(() => {
        axios.get(`tests/digital/sat/b2b/review/${testId}/`)
            .then(res => {
                // console.log(res.data);
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
                // console.log(testId);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (testModules) {
            setShortcutKeyPressed(true);

            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            // dragMove(document.getElementById('referenceDrag'), document.getElementById('referenceHeader'));
            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            const referenceDragEl = document.getElementById('referenceDrag');
            const referenceHeaderEl = document.getElementById('referenceHeader');
            if (referenceDragEl && referenceHeaderEl) {
                dragMove(referenceDragEl, referenceHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            generateAnnotation();
        }
    }, [testModules, moduleIndex, questionIndex, print]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen, annotations]);

    useEffect(() => {
        if (useDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (useDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (useDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (useDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    useEffect(() => {
        if (print) handlePrint();
    }, [print]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <div className='testReview__box' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    Test Review - Test Center Admin
                </title>
            </Helmet>
            {print && <div className='testReview__print' ref={printRef}>
                {testModules.map(testModule => (<>
                    <h2 className='testReview__title'>
                        {testModule.name} {testModule.hard && '(Hard)'}
                    </h2>
                    {testModule.questions.map((question, q) => (
                        <div className='testReview__test testReview__pageBreak'>
                            {question.passages.length > 0 && <>
                                <div className='testReview__material'>
                                    <div className='testReview__materialContent'>
                                        <div id='testReview__passages' className='testReview__passages'>
                                            {question.figures.map(figure => (
                                                <img
                                                    className='testReview__figure'
                                                    key={figure.id}
                                                    src={figure.image}
                                                    alt={(q + 1).toString()}
                                                />
                                            ))}
                                            {question.passages.map(({ id, passage }) => (
                                                <div key={id} className='testReview__passage'>
                                                    <p
                                                        id={id}
                                                        className='katex testReview__passageText'
                                                    >
                                                        {passage}
                                                    </p>
                                                    {testModule.answers[q].annotations.filter(annotation => !annotation.isStem && !annotation.isChoice).map((annotation, a) => <>
                                                        <div
                                                            className={`katex testReview__passageAnnotation
                                                                ${annotation.color && 'testReview__passageAnnotation--' + COLORS[annotation.color]}
                                                                ${annotation.underline && 'testReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                            `}
                                                            dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                        ></div>
                                                        {annotation.annotation &&
                                                            <p
                                                                className='testReview__passageAnnotationBubble'
                                                            >
                                                                {annotation.annotation}
                                                            </p>
                                                        }
                                                    </>)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className='testReview__verticalLine'></div>
                            </>}
                            <div className='testReview__question'>
                                <div className='testReview__questionContent'>
                                    <div className='testReview__questionHeader'>
                                        <p className={testModule.answers[q].isCorrect
                                            ? 'testReview__questionNumber--correct'
                                            : 'testReview__questionNumber--wrong'
                                        }>
                                            {q + 1}
                                        </p>
                                        <button className='testReview__questionMarkButton' onClick={() => {}}>
                                            {testModule.answers[q].marked
                                                ? <MdOutlineBookmark className='testReview__questionMarkIcon--marked' />
                                                : <MdOutlineBookmarkBorder className='testReview__questionMarkIcon' />
                                            }
                                            Mark for review
                                        </button>
                                        <button
                                            className={showAnswer
                                                ? 'testReview__showAnswer--active'
                                                : 'testReview__showAnswer'
                                            }
                                            onClick={() => setShowAnswer(!showAnswer)}
                                        >
                                            {showAnswer ? 'Hide answer' : 'Show answer'}
                                        </button>
                                    </div>
                                    <div id='testReview__questionChoices' className='testReview__questionChoices'>
                                        {testModule.subject === 'Math' &&
                                            question.figures.map(figure => (
                                                <img
                                                    className='testReview__figure'
                                                    key={figure.id}
                                                    src={figure.image}
                                                    alt={(q + 1).toString()}
                                                />
                                            ))
                                        }
                                        {testModule.subject === 'Math'
                                        ?   <p className='testReview__questionQuestion'>
                                                <span className='katex'>
                                                    {question.question}
                                                </span>
                                            </p>
                                        :   <div className='testReview__questionStem'>
                                                <p
                                                    id={question.id}
                                                    className='katex testReview__passageText testReview__questionText'
                                                >
                                                    {question.question}
                                                </p>
                                                {testModule.answers[q].annotations.filter(annotation => (
                                                    annotation.isStem && String(annotation.passageId) === String(question.id)
                                                )).map((annotation, a) => <>
                                                    <div
                                                        key={a}
                                                        className={`katex testReview__passageAnnotation
                                                            ${annotation.color && 'testReview__passageAnnotation--' + COLORS[annotation.color]}
                                                            ${annotation.underline && 'testReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                        `}
                                                        dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                    ></div>
                                                    {/* Always rendered, no hover: this is the print copy,
                                                        matching how the print passage overlay above it
                                                        shows its notes. */}
                                                    {annotation.annotation &&
                                                        <p className='testReview__passageAnnotationBubble'>
                                                            {annotation.annotation}
                                                        </p>
                                                    }
                                                </>)}
                                            </div>
                                        }
                                        {question.choices.length
                                        ?   <>
                                                {question.choices.map((choice, i) => (
                                                    <div key={choice.id} className='testReview__questionChoiceWrapper'>
                                                        <button
                                                            className={showAnswer && choice.isCorrect
                                                                ? 'testReview__questionChoice--correct'
                                                                : testModule.answers[q].answer == choice.id
                                                                ? (choice.isCorrect 
                                                                    ? 'testReview__questionChoice--selected'
                                                                    : 'testReview__questionChoice--wrong'
                                                                )
                                                                : 'testReview__questionChoice'
                                                            }
                                                            onClick={() => {}}
                                                        >
                                                            <div className='testReview__questionChoiceOutline'></div>
                                                            <p className='testReview__questionChoiceLetter'>
                                                                {MULTIPLE_CHOICES[i]}
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='testReview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='testReview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </button>
                                                    </div>
                                                ))}
                                            </>
                                        :   <div className='testReview__questionAnswerReview'>
                                                <TextInput
                                                    className='testReview__questionAnswer'
                                                    inputClassName='testReview__questionAnswerInput'
                                                    name={name + q}
                                                    value={testModule.answers[q].answer
                                                        ? testModule.answers[q].answer
                                                        : ''
                                                    }
                                                    onChange={() => {}}
                                                />
                                                {showAnswer &&
                                                    <p className='testReview__questionCorrectAnswer'>
                                                        {question.correctAnswers.toString().replaceAll(',', ', ')}
                                                    </p>
                                                }
                                            </div>
                                        }
                                        <div className='testReview__marginBottom'></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </>))}
            </div>}
            <div className='testReview'>
                <div className='testReview__header'>
                    <div className='testReview__titleGroup'>
                        <button className='testReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='testReview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='testReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='testReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='testReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'testReview__moduleSelectDropdownItem--selected'
                                                : 'testReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='testReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='testReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='testReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='testReview__timerGroup'>
                        <p className='testReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='testReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='testReview__actionGroup'>
                        {canRequestEdits(user) && questions[questionIndex].questionId &&
                            <a
                                className='testReview__action testReview__editRequest'
                                href={buildEditRequestUrl(questions[questionIndex].questionId, user)}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <MdOutlineFlag className='testReview__actionIcon' />
                                <p className='testReview__actionText'>Edit Request</p>
                            </a>
                        }
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) && 
                            <button
                                className='testReview__action'
                                onClick={() => setPrint(true)}
                            >
                                <MdOutlinePrint className='testReview__actionIcon' />
                                <p className='testReview__actionText'>Print</p>
                            </button>
                        }
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen
                                    ? 'testReview__action--active'
                                    : 'testReview__action'
                                }
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='testReview__actionIcon' />
                                <p className='testReview__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen
                                    ? 'testReview__action--active'
                                    : 'testReview__action'
                                }
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='testReview__actionIcon' />
                                <p className='testReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className={shortcutsOpen
                                ? 'testReview__action--active'
                                : 'testReview__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (referenceOpen) setReferenceOpen(false);
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='testReview__actionIcon' />
                            <p className='testReview__actionText'>Shortcuts</p>
                        </button>
                        <button
                            className='testReview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='testReview__actionIcon' />
                            <p className='testReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='testReview__directions' ref={directionsRef}>
                            <div className='testReview__directionsBubble'>
                                <p className='katex testReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='testReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <DesmosCalculatorPanel
                        open={calculatorOpen}
                        onClose={() => setCalculatorOpen(false)}
                        headerClassName='testReview__popupHeader'
                    />
                    <div id='referenceDrag' className={referenceOpen
                        ? 'testReview__popup--reference'
                        : 'testReview__popup--hidden'
                    }>
                        <div id='referenceHeader' className='testReview__popupHeader'>
                            <h3 className='testReview__popupHeaderText'>Reference</h3>
                            <MdClose className='testReview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'testReview__popup--shortcuts'
                        : 'testReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='testReview__popupHeader'>
                            <h3 className='testReview__popupHeaderText'>
                                Keyboard shortcuts{useDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : useDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>
                            <MdClose className='testReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'testReview__popup--explanation'
                        : 'testReview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='testReview__popupHeader'>
                            <h3 className='testReview__popupHeaderText'>Explanation</h3>
                            <MdClose className='testReview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='testReview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt='Explanation Figure'
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='testReview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testReview__dottedLine'></div>
                <div className='testReview__test'>
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='testReview__material'>
                            <div className='testReview__materialContent'>
                                <div id='testReview__passages' className='testReview__passages'>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='testReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }) => (
                                        <div key={id} className='testReview__passage'>
                                            <p
                                                id={id}
                                                className='katex testReview__passageText'
                                            >
                                                {passage}
                                            </p>
                                            {annotations.filter(annotation => !annotation.isStem && !annotation.isChoice).map((annotation, a) => <>
                                                <div
                                                    className={`katex testReview__passageAnnotation
                                                        ${annotation.color && 'testReview__passageAnnotation--' + COLORS[annotation.color]}
                                                        ${annotation.underline && 'testReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                    `}
                                                    onMouseOver={(event) => handleAnnotationMouseOver(event, `testReview__passageAnnotationBubble${a}`)}
                                                    onMouseOut={(event) => handleAnnotationMouseOut(event, `testReview__passageAnnotationBubble${a}`)}
                                                    dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                ></div>
                                                {annotation.annotation &&
                                                    <p
                                                        id={`testReview__passageAnnotationBubble${a}`}
                                                        className='testReview__passageAnnotationBubble'
                                                    >
                                                        {annotation.annotation}
                                                    </p>
                                                }
                                            </>)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='testReview__verticalLine'></div>
                    </>}
                    <div className='testReview__question'>
                        <div className='testReview__questionContent'>
                            <div className='testReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'testReview__questionNumber--correct'
                                    : 'testReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='testReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='testReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='testReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                    <button
                                        className={showExplanation
                                            ? 'testReview__explanation--active'
                                            : 'testReview__explanation'
                                        }
                                        onClick={() => setShowExplanation(!showExplanation)}
                                    >
                                        Explanation
                                    </button>
                                }
                                <button
                                    className={showAnswer
                                        ? 'testReview__showAnswer--active'
                                        : 'testReview__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>
                            <div id='testReview__questionChoices' className='testReview__questionChoices'>
                                {subject === 'Math' &&
                                    questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='testReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))
                                }
                                {subject === 'Math'
                                ?   <p className='testReview__questionQuestion'>
                                        <span className='katex'>
                                            {questions[questionIndex].question}
                                        </span>
                                    </p>
                                :   <div className='testReview__questionStem'>
                                        <p
                                            id={questions[questionIndex].id}
                                            className='katex testReview__passageText testReview__questionText'
                                        >
                                            {questions[questionIndex].question}
                                        </p>
                                        {annotations.filter(annotation => (
                                            annotation.isStem && String(annotation.passageId) === String(questions[questionIndex].id)
                                        )).map((annotation, a) => <>
                                            <div
                                                key={a}
                                                className={`katex testReview__passageAnnotation
                                                    ${annotation.color && 'testReview__passageAnnotation--' + COLORS[annotation.color]}
                                                    ${annotation.underline && 'testReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                `}
                                                onMouseOver={(event) => handleAnnotationMouseOver(event, `testReview__stemAnnotationBubble${a}`)}
                                                onMouseOut={(event) => handleAnnotationMouseOut(event, `testReview__stemAnnotationBubble${a}`)}
                                                dangerouslySetInnerHTML={{ __html: annotation.html }}
                                            ></div>
                                            {/* The note, on hover -- the bubble passages have always
                                                had. The id is stem-specific because `a` restarts at 0
                                                for every filtered list. */}
                                            {annotation.annotation &&
                                                <p
                                                    id={`testReview__stemAnnotationBubble${a}`}
                                                    className='testReview__passageAnnotationBubble'
                                                >
                                                    {annotation.annotation}
                                                </p>
                                            }
                                        </>)}
                                    </div>
                                }
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='testReview__questionChoiceWrapper'>
                                                <button
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'testReview__questionChoice--correct'
                                                        : answers[questionIndex].answer == choice.id
                                                        ? (choice.isCorrect 
                                                            ? 'testReview__questionChoice--selected'
                                                            : 'testReview__questionChoice--wrong'
                                                        )
                                                        : 'testReview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='testReview__questionChoiceOutline'></div>
                                                    <p className='testReview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='testReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   /* Mirrors the module's choice markup so saved
                                                           highlights land in the same place: the wrapper is
                                                           the overlay's positioning context and `.katex`
                                                           sits on the text element itself. Read-only. */
                                                        <span className='testReview__questionChoiceTextWrapper'>
                                                            <p
                                                                id={`choice-${choice.id}`}
                                                                className='katex testReview__passageText testReview__questionChoiceText'
                                                            >
                                                                {choice.choice}
                                                            </p>
                                                            {annotations.filter(annotation => (
                                                                annotation.isChoice
                                                                && String(annotation.passageId) === String(choice.id)
                                                            )).map((annotation, a) => <>
                                                                <span
                                                                    key={`ann-${choice.id}-${a}`}
                                                                    className={`katex testReview__passageAnnotation
                                                                        ${annotation.color && 'testReview__passageAnnotation--' + COLORS[annotation.color]}
                                                                        ${annotation.underline && 'testReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                                    `}
                                                                    onMouseOver={(event) => handleAnnotationMouseOver(event, `testReview__choiceAnnotationBubble${choice.id}-${a}`)}
                                                                    onMouseOut={(event) => handleAnnotationMouseOut(event, `testReview__choiceAnnotationBubble${choice.id}-${a}`)}
                                                                    dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                                ></span>
                                                                {/* The note, revealed on hover -- same bubble the
                                                                    passage overlays use. The id carries the choice
                                                                    id because `a` restarts at 0 per filtered list,
                                                                    so a passage-style id would collide. */}
                                                                {annotation.annotation &&
                                                                    <p
                                                                        id={`testReview__choiceAnnotationBubble${choice.id}-${a}`}
                                                                        className='testReview__passageAnnotationBubble'
                                                                    >
                                                                        {annotation.annotation}
                                                                    </p>
                                                                }
                                                            </>)}
                                                        </span>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                :   <div className='testReview__questionAnswerReview'>
                                        <TextInput
                                            className='testReview__questionAnswer'
                                            inputClassName='testReview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={answers[questionIndex].answer
                                                ? answers[questionIndex].answer
                                                : ''
                                            }
                                            onChange={() => {}}
                                        />
                                        {showAnswer &&
                                            <p className='testReview__questionCorrectAnswer'>
                                                {questions[questionIndex].correctAnswers.toString().replaceAll(',', ', ')}
                                            </p>
                                        }
                                    </div>
                                }
                                <div className='testReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testReview__dottedLine'></div>
                <div className='testReview__footer'>
                    <p className='testReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='testReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='testReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='testReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='testReview__buttons'>
                        <Button
                            className='testReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='testReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='testReview__navigator' ref={navigatorRef}>
                            <div className='testReview__navigatorBubble'>
                                <h2 className='testReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='testReview__navigatorLegends'>
                                    <div className='testReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='testReview__navigatorLegendCurrent' />
                                        <p className='testReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='testReview__navigatorLegend'>
                                        <div className='testReview__navigatorLegendCorrect'></div>
                                        <p className='testReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='testReview__navigatorLegend'>
                                        <div className='testReview__navigatorLegendWrong'></div>
                                        <p className='testReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='testReview__navigatorLegend'>
                                        <MdOutlineBookmark className='testReview__navigatorLegendMarked' />
                                        <p className='testReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='testReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'testReview__navigatorItem--correct'
                                                : 'testReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='testReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='testReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='testReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='testReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default DigitalSATReview;